import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { activeClientState, reportCutoffState } from '../state.js';
import { useApi } from '../hooks.js';
import { AdvisorSummaryBlock, usePublishedAdvisorSummary } from '../components/advisor/AdvisorSummaryBlock.js';
import { canPrintWith } from '../components/advisor/advisorSummaryState.js';
import { Loading } from '../components/Loading.js';
import { FundLogo } from '../components/FundChip.js';
import { CutoffDropdown } from '../components/CutoffDropdown.js';
import { KpiCards, kpiFromNumbers } from '../components/KpiCards.js';
import {
  PortfolioReturnChart,
  TotalAssetsChart,
  portfolioReturnHasData,
  totalAssetsHasData,
  type PortfolioMonth,
  type PortfolioYearStat,
} from '../components/PortfolioSummaryCharts.js';
import { BenchmarkChart, benchmarkHasData } from '../components/BenchmarkChart.js';
import { portfolioFrom } from '../returnsFrom.js';
import { AllocationPie, allocationHasData } from '../components/AllocationPie.js';
import { RikuzTable, rikuzHasData } from '../components/RikuzTable.js';
import { LinkagePie, AssetClassPie, linkageHasData, assetClassHasData } from '../components/BreakdownPies.js';
import { ManagersMatrix, managersMatrixHasData } from '../components/ManagersMatrix.js';
import { ManagersReturnsTable, managersReturnsHasData } from '../components/ManagersTables.js';
import { PolicyControlTable, policyHasData } from '../components/PolicyControlTable.js';
import { ReportNotes, DisclosurePage } from '../components/ReportDisclosure.js';
import { PrintScopeContext } from '../printScope.js';
import { activeFunds } from '../activeFunds.js';
import { onlyVisibleFunds } from '../visibleFunds.js';
import { withStatementReturns } from '../statementReturns.js';
import { FundCurrencyProvider, type FundCurrencyRow } from '../money.js';
import type { ClientInfo, PerformanceRow, PolicyReportView, RikuzLineRow } from '@agio/core';
import type { FundOption } from '../components/FundDropdown.js';
import type { AltInvestmentView } from '@agio/core/domain';
import { AltBenchmarkNote, AltReportProvider } from '../altReports.js';
import { AlternativeFundPanel, alternativeHasData } from '../components/AlternativeFundPanel.js';

interface BenchRow {
  date: string;
  fund_id: string;
  monthly_benchmark: number | null;
  cumulative_benchmark: number | null;
}

/**
 * Printable report (light theme, browser print). The סיכום section is ALWAYS
 * included; the admin picks which funds get a per-fund page. The toolbar and
 * app chrome disappear in print (@media print).
 */
export function PrintPage() {
  const clientId = useRecoilValue(activeClientState);
  const cutoff = useRecoilValue(reportCutoffState);
  const asOfQ = cutoff ? `?asOf=${encodeURIComponent(cutoff)}` : '';
  const { data: clients } = useApi<ClientInfo[]>('/clients');
  const { data: funds, loading } = useApi<FundOption[]>(clientId ? `/clients/${clientId}/funds` : null);
  const { data: currencies } = useApi<FundCurrencyRow[]>(clientId ? `/clients/${clientId}/currencies` : null);
  const { data: rikuz } = useApi<RikuzLineRow[]>(clientId ? `/clients/${clientId}/rikuz${asOfQ}` : null);
  const { data: perf, loading: perfLoading } = useApi<PerformanceRow[]>(clientId ? `/clients/${clientId}/performance${asOfQ}` : null);
  const { data: bench } = useApi<BenchRow[]>(clientId ? `/clients/${clientId}/benchmark${asOfQ}` : null);
  const { data: portfolio } = useApi<{ months: PortfolioMonth[]; years: PortfolioYearStat[] }>(
    clientId ? `/clients/${clientId}/portfolio${asOfQ}` : null,
  );
  const { data: policyView } = useApi<PolicyReportView | null>(
    clientId ? `/clients/${clientId}/policy${cutoff ? `?period=${encodeURIComponent(cutoff.slice(0, 7))}` : ''}` : null,
  );
  const { data: alternatives } = useApi<AltInvestmentView[]>(clientId ? `/clients/${clientId}/alternatives${asOfQ}` : null);
  // The advisor's published summary opens the report, right under the title.
  const advisorState = usePublishedAdvisorSummary(clientId);
  const advisorSummary = advisorState.status === 'ready' ? advisorState.summary : null;
  // Printing waits until we KNOW whether there is a summary — a report printed
  // while it is still loading would silently leave it out.
  const printReady = canPrintWith(advisorState);

  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const client = (clients ?? []).find((c) => c.client_id === clientId);
  // The first month the return charts show — the admin's choice for this client, same as on screen.
  const returnsFrom = client?.returns_from_month ?? null;
  const fundList = funds ?? [];
  const selectedFunds = fundList.filter((f) => !excluded.has(f.fund_id));

  const { summaryLines, managerLines, current, balanceAsOf } = useMemo(() => {
    // With a cutoff picked, the server already returned each fund's own row AT OR
    // BEFORE that date — is_latest (the true, unbounded latest) no longer applies.
    const latest = (rikuz ?? []).filter((l) => (cutoff ? true : l.is_latest));
    const maxDate = latest.reduce<string | null>((a, l) => (l.report_date && (!a || l.report_date > a) ? l.report_date : a), null);
    const current = latest.filter((l) => l.report_date === maxDate);
    return {
      summaryLines: aggregate(current),
      // Per-fund rows (pre-aggregation) — what ManagersMatrix needs to build a row per manager.
      managerLines: onlyVisibleFunds(current, funds ?? []),
      current,
      // The month the printed balance belongs to. Not the same as the month of
      // the printed return, which needs confirmed deposits and withdrawals
      // before it exists at all.
      balanceAsOf: maxDate,
    };
  }, [rikuz, cutoff, funds]);

  if (!clientId) return <div className="py-24 text-center text-muted-foreground">בחר לקוח לפני הדפסה.</div>;
  if (loading || !portfolio) return <Loading />;

  const pm = portfolio.months.filter((m) => m.monthly_return !== null);
  // The benchmark has its own series and can end in a different month from the
  // return. Picked once here, so its value and its caption come off the same
  // row — the same rule the portal now follows.
  const lastBench = portfolio.months.filter((m) => m.benchmark_monthly !== null).at(-1) ?? null;
  // ריכוז only ever carries a fund's CURRENT holdings snapshot, not one per month — so
  // at an earlier cutoff it usually has nothing yet. Fall back to the whole-portfolio
  // asset-volume series (same source the "total assets" chart already uses below), which
  // IS a real monthly history. Only fills the gap; never overrides a real value.
  const totalValue = summaryLines[0]?.total_value ?? portfolio.months.at(-1)?.total_volume ?? null;
  // The funds behind that total, from the same source (see activeFunds.ts).
  const fundCount = activeFunds(current, perfLoading || perf === null ? null : perf, portfolio.months.at(-1)?.date ?? null)?.length ?? null;
  // The returns table names funds one by one: funds an admin switched off stay out.
  // Statement months get their returns worked out (see statementReturns.ts) so
  // the printed returns reach the same month as the printed balance.
  const shownPerf = withStatementReturns(perf ?? [], bench ?? []);
  const visiblePerf = onlyVisibleFunds(shownPerf, fundList);
  const hasAlternatives = (alternatives ?? []).some((a) => a.statements.length > 0);

  // Report title — opens the report, whichever page comes first.
  const reportHeader = (
    <div className="print-section mb-6 border-b-2 border-(--print-navy) pb-4">
      <h1 className="text-3xl font-bold">{client?.display_name_he ?? clientId}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        דוח תיק השקעות · הופק {new Date().toLocaleDateString('he-IL')} · agio
      </p>
    </div>
  );

  return (
    <FundCurrencyProvider funds={currencies ?? []}>
    <AltReportProvider perf={shownPerf}>
    <div className="light-scope -mx-6 -my-8 min-h-screen px-8 py-8 print:m-0 print:p-0">
      {/* Print setup — THE page on screen; the report itself renders off-viewport. */}
      <div className="no-print mx-auto max-w-2xl py-10">
        <h1 className="text-2xl font-bold">הדפסת דוח · {client?.display_name_he ?? clientId}</h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground">
          בחרו אילו בתי השקעות יקבלו עמוד בדוח. סיכום התיק נכלל תמיד.
        </p>
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-3 border-b border-border pb-4">
            <span className="text-sm font-medium text-muted-foreground">עבור חודש</span>
            <CutoffDropdown clientId={clientId} />
          </div>
          <div className="grid gap-1 sm:grid-cols-2">
            {fundList.map((f) => (
              <label
                key={f.fund_id}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted/60"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!excluded.has(f.fund_id)}
                  onChange={(e) => {
                    const next = new Set(excluded);
                    if (e.target.checked) next.delete(f.fund_id);
                    else next.add(f.fund_id);
                    setExcluded(next);
                  }}
                />
                <FundLogo name={f.fund_name} size={22} />
                <span className="font-medium">{f.fund_name}</span>
              </label>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between border-t border-border pt-4">
            <span className="text-sm text-muted-foreground">
              {selectedFunds.length} בתי השקעות בדוח + סיכום · {new Date().toLocaleDateString('he-IL')}
            </span>
            <button
              onClick={() => window.print()}
              disabled={!printReady}
              aria-busy={advisorState.status === 'loading'}
              className="rounded-md bg-primary px-8 py-2.5 font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {advisorState.status === 'loading' ? 'טוען…' : '🖨 הדפס'}
            </button>
          </div>
          {advisorState.status === 'error' && (
            <p role="alert" className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              לא ניתן היה לטעון את סקירת היועץ, ולכן ההדפסה נעצרה — אחרת הדוח היה יוצא בלעדיה. רעננו את הדף ונסו שוב.
              <span className="mt-1 block text-xs" dir="auto">{advisorState.error}</span>
            </p>
          )}
        </div>
      </div>

      <PrintScopeContext.Provider value={true}>
      <div className="print-offscreen" aria-hidden>

      {/* The whole report lives in a table: the thead/tfoot repeat on EVERY printed
          page (browser print behavior), giving each page the brand header/footer band.
          table-layout:fixed is load-bearing — without it, this table's auto-layout
          sizes itself to its widest descendant's UNWRAPPED content (the wide
          whitespace-nowrap matrix/returns tables), stretching the entire report past
          the page width and sliding every chart and section off the printed page. */}
      <table className="w-full" style={{ tableLayout: 'fixed' }}>
        <thead className="hidden print:table-header-group">
          <tr>
            <td className="pb-4">
              <img src="/brand/print-header.jpeg" alt="AGIO" className="print-band block w-full" />
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
      {/* First page — title, the headline numbers, and the report notes sitting on
          the foot of the page. Exactly one printed page tall, so the notes land at
          the bottom of page 1 and the charts below start page 2 (where they already
          fell on their own). */}
      {/* With an advisor summary, the title and the summary open the report on
          their own sheet(s) — a long summary simply runs on — and the first page
          below follows unchanged, notes still pinned to its foot. */}
      {advisorSummary && (
        <div className="print-advisor-page print-page-fill">
          {reportHeader}
          <AdvisorSummaryBlock summary={advisorSummary} variant="print" />
        </div>
      )}
      <div className="print-first-page">
        {!advisorSummary && reportHeader}

        {/* סיכום — always included */}
        <div className="print-section flex flex-col gap-6">
          <h2 className="print-heading">סיכום תיק</h2>
          <KpiCards
            kpis={kpiFromNumbers({
              totalValue,
              monthlyReturn: pm.at(-1)?.monthly_return ?? null,
              monthlyBenchmark: lastBench?.benchmark_monthly ?? null,
              asOf: pm.at(-1)?.date ?? null,
              benchAsOf: lastBench?.date ?? null,
              balanceAsOf,
              fundCount,
            })}
          />
        </div>

        <ReportNotes />
      </div>

      <div className="print-section flex flex-col gap-6">
        {portfolioReturnHasData(portfolio.months) && <PortfolioReturnChart {...portfolioFrom(portfolio.months, portfolio.years, returnsFrom)} />}
        {hasAlternatives && <AltBenchmarkNote />}
        {totalAssetsHasData(portfolio.months) && <TotalAssetsChart months={portfolio.months} />}
        {(allocationHasData(summaryLines) || rikuzHasData(summaryLines)) && (
          <div className="grid gap-6 lg:grid-cols-2 print:grid-cols-2">
            {allocationHasData(summaryLines) && <AllocationPie lines={summaryLines} />}
            {rikuzHasData(summaryLines) && <RikuzTable lines={summaryLines} />}
          </div>
        )}
      </div>

      {/* ריכוז התפלגות לפי אפיקים — allocation page, sample report order */}
      {(linkageHasData(summaryLines) || assetClassHasData(summaryLines) || managersMatrixHasData(managerLines)) && (
        <div className="print-page print-section mt-10 flex flex-col gap-6 border-t border-border pt-8">
          <h2 className="print-heading">ריכוז התפלגות לפי אפיקים</h2>
          {(linkageHasData(summaryLines) || assetClassHasData(summaryLines)) && (
            <div className="grid gap-6 md:grid-cols-2 print:grid-cols-2">
              {linkageHasData(summaryLines) && <LinkagePie lines={summaryLines} />}
              {assetClassHasData(summaryLines) && <AssetClassPie lines={summaryLines} />}
            </div>
          )}
          {managersMatrixHasData(managerLines) && <ManagersMatrix lines={managerLines} />}
        </div>
      )}

      {/* דוח תשואות — returns page, sample report order */}
      {managersReturnsHasData(visiblePerf) && (
        <div className="print-page print-section mt-10 flex flex-col gap-6 border-t border-border pt-8">
          <h2 className="print-heading">דוח תשואות</h2>
          <ManagersReturnsTable perf={visiblePerf} />
        </div>
      )}

      {/* דו"ח בקרת מדיניות — the published policy table for the month, when there is one */}
      {policyHasData(policyView) && (
        <div className="print-page print-section mt-10 flex flex-col gap-6 border-t border-border pt-8">
          <h2 className="print-heading">דו"ח בקרת מדיניות</h2>
          <PolicyControlTable view={policyView} />
        </div>
      )}

      {/* per-fund pages — only the selected funds, and only when there's anything to show */}
      {selectedFunds.map((f) => {
        const fPerf = shownPerf.filter((p) => p.fund_id === f.fund_id);
        const fBench = (bench ?? []).filter((b) => b.fund_id === f.fund_id);
        const fLines = (rikuz ?? []).filter((l) => l.fund_id === f.fund_id && (cutoff ? true : l.is_latest));
        const fundHasAnyData = fLines.length > 0 || fPerf.length > 0 || fBench.length > 0;
        if (!fundHasAnyData) return null;
        return (
          <div key={f.fund_id} className="print-fund print-section mt-10 flex flex-col gap-6 border-t border-border pt-8">
            <h2 className="print-heading flex items-center justify-center gap-3">
              <FundLogo name={f.fund_name} size={28} />
              {f.fund_name}
            </h2>
            {f.kind === 'alternative'
              ? alternativeHasData(fPerf) && (
                <AlternativeFundPanel fundId={f.fund_id} perf={fPerf}
                  investment={(alternatives ?? []).find((a) => a.fund_id === f.fund_id) ?? null} />
              )
              : benchmarkHasData(fPerf, fBench) && <BenchmarkChart perf={fPerf} bench={fBench} from={returnsFrom} />}
            {rikuzHasData(fLines) && <RikuzTable lines={fLines} />}
          </div>
        );
      })}

      {/* last page — the full גילוי נאות, referred to from the first page's notes */}
      <DisclosurePage />
            </td>
          </tr>
        </tbody>
        <tfoot className="hidden print:table-footer-group">
          <tr>
            <td className="pt-4">
              <img src="/brand/print-footer.jpeg" alt="" className="print-band block w-full" />
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
      </PrintScopeContext.Provider>
    </div>
    </AltReportProvider>
    </FundCurrencyProvider>
  );
}

/** Same aggregation as the summary view: sum sleeves across current-month snapshots. */
function aggregate(lines: RikuzLineRow[]): RikuzLineRow[] {
  if (lines.length === 0) return [];
  const bySleeve = new Map<string, RikuzLineRow>();
  for (const l of lines) {
    const cur = bySleeve.get(l.sleeve);
    // A summed line belongs to no single fund, so it is never written in one fund's currency.
    if (!cur) bySleeve.set(l.sleeve, { ...l, report_id: `agg|${l.sleeve}`, fund_id: 'agg' });
    else cur.value = (cur.value ?? 0) + (l.value ?? 0);
  }
  const sleeves = [...bySleeve.values()].sort((a, b) => a.sleeve_order - b.sleeve_order);
  const total = sleeves.reduce((a, s) => a + (s.value ?? 0), 0);
  return sleeves.map((s) => ({ ...s, total_value: total, pct_of_total: total ? (s.value ?? 0) / total : null }));
}
